import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface AlertCountBySection {
  menu_section: string;
  alert_count: number;
  critical_count: number;
  warning_count: number;
  error_count: number;
}

interface AlertCounts {
  campaigns: {
    total: number;
    critical: number;
    warning: number;
    error: number;
  };
  cronjob_monitor: {
    total: number;
    critical: number;
    warning: number;
    error: number;
    disabled: number;
  };
  email_debugger: {
    total: number;
    critical: number;
  };
  webhooks: {
    total: number;
    critical: number;
    warning: number;
    error: number;
  };
  user_errors: {
    total: number;
    generation_errors: number;
  };
  social_media: {
    total: number;
    critical: number;
    warning: number;
    error: number;
  };
  signups: {
    total: number;
    critical: number;
    warning: number;
    error: number;
  };
}

const DEFAULT_COUNTS = {
  campaigns: { total: 0, critical: 0, warning: 0, error: 0 },
  cronjob_monitor: { total: 0, critical: 0, warning: 0, error: 0, disabled: 0 },
  email_debugger: { total: 0, critical: 0 },
  webhooks: { total: 0, critical: 0, warning: 0, error: 0 },
  user_errors: { total: 0, generation_errors: 0 },
  social_media: { total: 0, critical: 0, warning: 0, error: 0 },
  signups: { total: 0, critical: 0, warning: 0, error: 0 },
};

export function useAlertCounts() {
  const [alertCounts, setAlertCounts] = useState<AlertCounts>(DEFAULT_COUNTS);
  const [loading, setLoading] = useState(true);

  const fetchAlertCounts = async () => {
    try {
      // Get alert counts by menu section (cronjob alerts)
      const { data: alertData, error: alertError } = await supabase.rpc('get_alert_counts_by_menu');

      if (alertError) {
        console.error('Error fetching alert counts:', alertError);
        return;
      }

      // Get campaign-specific alerts
      const { data: campaignAlertData, error: campaignAlertError } = await supabase.rpc('get_campaign_alert_counts');

      if (campaignAlertError) {
        console.error('Error fetching campaign alert counts:', campaignAlertError);
      }

      // Get email health status for email debugger
      const { data: emailHealth, error: emailError } = await supabase
        .from('system_health_status')
        .select('total_unreviewed_errors')
        .maybeSingle();

      if (emailError) {
        console.error('Error fetching email health:', emailError);
      }

      // Get disabled cronjobs count
      const { count: disabledCount, error: disabledError } = await supabase
        .from('cronjob_settings')
        .select('*', { count: 'exact', head: true })
        .eq('enabled', false);

      if (disabledError) {
        console.error('Error fetching disabled cronjobs count:', disabledError);
      }

      // Get user errors count
      const { count: totalErrorsCount, error: totalErrorsError } = await supabase
        .from('user_errors')
        .select('*', { count: 'exact', head: true })
        .is('resolved_at', null);

      if (totalErrorsError) {
        console.error('Error fetching total user errors count:', totalErrorsError);
      }

      // Get generation errors count
      const { count: generationErrorsCount, error: generationErrorsError } = await supabase
        .from('user_errors')
        .select('*', { count: 'exact', head: true })
        .is('resolved_at', null)
        .not('generation_type', 'is', null);

      if (generationErrorsError) {
        console.error('Error fetching generation errors count:', generationErrorsError);
      }

      // Transform data into menu-specific counts
      const counts: AlertCounts = { ...DEFAULT_COUNTS };

      if (alertData && Array.isArray(alertData)) {
        alertData.forEach((row: AlertCountBySection) => {
          if (row.menu_section === 'campaigns') {
            counts.campaigns = {
              total: row.alert_count,
              critical: row.critical_count,
              warning: row.warning_count,
              error: row.error_count,
            };
          } else if (row.menu_section === 'cronjob_monitor') {
            counts.cronjob_monitor = {
              total: row.alert_count,
              critical: row.critical_count,
              warning: row.warning_count,
              error: row.error_count,
              disabled: disabledCount || 0,
            };
          } else if (row.menu_section === 'webhooks') {
            counts.webhooks = {
              total: row.alert_count,
              critical: row.critical_count,
              warning: row.warning_count,
              error: row.error_count,
            };
          } else if (row.menu_section === 'social_media') {
            counts.social_media = {
              total: row.alert_count,
              critical: row.critical_count,
              warning: row.warning_count,
              error: row.error_count,
            };
          } else if (row.menu_section === 'signups') {
            counts.signups = {
              total: row.alert_count,
              critical: row.critical_count,
              warning: row.warning_count,
              error: row.error_count,
            };
          }
        });
      }

      // If no cronjob_monitor alerts exist but we have disabled jobs, still set the count
      if (counts.cronjob_monitor.total === 0 && disabledCount && disabledCount > 0) {
        counts.cronjob_monitor.disabled = disabledCount;
      }

      // Add campaign alerts from admin_campaign_alerts_view
      if (campaignAlertData && campaignAlertData.length > 0) {
        const campaignAlert = campaignAlertData[0];
        counts.campaigns = {
          total: Number(campaignAlert.total_alerts || 0),
          critical: Number(campaignAlert.critical_count || 0),
          warning: Number(campaignAlert.warning_count || 0),
          error: 0,
        };
      }

      // Add email debugger counts from system_health_status
      if (emailHealth) {
        counts.email_debugger = {
          total: emailHealth.total_unreviewed_errors || 0,
          critical: emailHealth.total_unreviewed_errors || 0,
        };
      }

      // Add user errors counts
      counts.user_errors = {
        total: totalErrorsCount || 0,
        generation_errors: generationErrorsCount || 0,
      };

      setAlertCounts(counts);
    } catch (err) {
      console.error('Error in fetchAlertCounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlertCounts();

    // Poll every 30 seconds
    const pollInterval = setInterval(fetchAlertCounts, 30000);

    // Set up real-time subscription for cronjob_alerts
    const alertsChannel = supabase
      .channel('admin-alerts')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cronjob_alerts',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    // Set up real-time subscription for email_send_logs (for debugger badge)
    const emailLogsChannel = supabase
      .channel('admin-email-logs')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_send_logs',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    // Set up real-time subscription for email_campaigns (for campaign alerts badge)
    const campaignsChannel = supabase
      .channel('admin-campaign-status')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_campaigns',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    // Set up real-time subscription for plugandpay_webhooks (for webhooks badge)
    const webhooksChannel = supabase
      .channel('admin-webhooks')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'plugandpay_webhooks',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    // Set up real-time subscription for user_errors (for user errors badge)
    const userErrorsChannel = supabase
      .channel('admin-user-errors')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_errors',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    // Set up real-time subscription for social_media_posts (for social media badge)
    const socialMediaChannel = supabase
      .channel('admin-social-media')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_media_posts',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    // Set up real-time subscription for signup_requests (for signups badge)
    const signupsChannel = supabase
      .channel('admin-signups')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'signup_requests',
        },
        () => {
          fetchAlertCounts();
        }
      )
      .subscribe();

    return () => {
      clearInterval(pollInterval);
      supabase.removeChannel(alertsChannel);
      supabase.removeChannel(emailLogsChannel);
      supabase.removeChannel(campaignsChannel);
      supabase.removeChannel(webhooksChannel);
      supabase.removeChannel(userErrorsChannel);
      supabase.removeChannel(socialMediaChannel);
      supabase.removeChannel(signupsChannel);
    };
  }, []);

  return { alertCounts, loading, refresh: fetchAlertCounts };
}
